import { ApiError, request, requestRaw } from '@/api/client'
import { env } from '@/config/env'
import type { Permission } from '@shared/permissions'
import { readSavedCompany } from '@/features/auth/company'

export interface CurrentUser {
  username: string
  userId: number | null
  displayName: string | null
  branchId: number | null
  vsoftRoleId: number | null
  roleKey: string | null
  permissions: Permission[]
  /** Pages closed for this user (صلاحيات المستخدمين); the sidebar and the router both go by it. */
  hiddenPages?: string[]
  /** Pages given «مسموح» to this user by his group or himself: shown even when the global show/hide table hides them. */
  allowedPages?: string[]
  profile: Record<string, unknown>
  sessionExpiresAt: number
}

export interface UserBranch {
  branchID?: number
  branchId?: number
  branchName?: string
  [key: string]: unknown
}

/**
 * The second sign-in step the gateway asks for after a right password (docs/auth-and-permissions.md «الدخول بخطوتين»):
 * a code from the authenticator app, or turning TOTP on when the company requires it. `challenge` / `enrollment` is the
 * gateway's own opaque reference; the backend's challenge never reaches the browser.
 */
export type SignInStep =
  | { kind: 'totp'; challenge: string; expiresAt: number }
  | { kind: 'enroll'; enrollment: string; expiresAt: number }

type LoginAnswer = CurrentUser & { twoFactorRequired?: boolean; enrollmentRequired?: boolean; challenge?: string; enrollment?: string; expiresAt?: number }

/** A sign-in that ended in a session, with what the code step adds: recovery codes (enrolment) or that one was used. */
export type SignedIn = CurrentUser & { recoveryCodes?: string[]; usedRecoveryCode?: boolean }

/*
 * Without the gateway (VITE_API_MODE=direct, docs/NO_GATEWAY.md) the same calls go to the backend: the compat sign-in
 * (/api/Users/V1/Login*) with `x-corex-session: cookie`, so the backend keeps the session in an httpOnly cookie and the
 * answer carries no token; who is signed in from /admin/v1/web/me; branches, branch switch, password and sign-out on the
 * compat Users controller. The answers are turned into the gateway's shapes here, so the screens see no difference.
 */
const COOKIE_SESSION = { 'x-corex-session': 'cookie' }
type Row = Record<string, unknown>
const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])
const numOrNull = (v: unknown) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v))
const expiry = (v: unknown, fallbackMs: number) => {
  const n = typeof v === 'number' ? v : Date.parse(String(v ?? ''))
  return Number.isFinite(n) && n > 0 ? n : Date.now() + fallbackMs
}

/** The signed-in user from a direct-mode sign-in answer: the profile fields and the access layers beside them. */
export function userFromLogin(data: Row): CurrentUser {
  const access = (data.access && typeof data.access === 'object' ? data.access : {}) as Row
  const { access: _a, sections: _s, userSession: _u, accessToken: _t, sessionExpiresAt, recoveryCodes: _r, usedRecoveryCode: _x, ...profile } = data
  return {
    username: String(access.username ?? data.userName ?? data.email ?? ''),
    userId: numOrNull(data.userId ?? data.userID),
    displayName: (data.displayName ?? data.dispalyName ?? null) as string | null,
    branchId: numOrNull(data.branchID ?? data.branchId),
    vsoftRoleId: numOrNull(data.roleID ?? data.roleId),
    roleKey: (access.roleKey as string | null | undefined) ?? null,
    permissions: arr(access.permissions) as Permission[],
    hiddenPages: arr(access.hiddenPages),
    allowedPages: arr(access.allowedPages),
    profile,
    sessionExpiresAt: expiry(sessionExpiresAt, 12 * 3600_000),
  }
}

/** One direct-mode sign-in call: the compat answer as it is, read here (a refusal is not a lost session). */
async function compatSignIn(path: string, body: unknown, headers: Record<string, string> = {}): Promise<Row> {
  const res = await requestRaw(path, { method: 'POST', body, headers: { ...COOKIE_SESSION, ...headers }, rawEnvelope: true })
  const answer = ((await res.json().catch(() => null)) ?? {}) as Row
  const message = typeof answer.message === 'string' && answer.message ? answer.message : ''
  const own = (answer.error && typeof answer.error === 'object' ? answer.error : null) as { code?: string; message?: string } | null
  if (res.status === 429) throw new ApiError(429, 'RATE_LIMITED', message || own?.message || 'محاولات كثيرة، حاول بعد قليل')
  if (res.status >= 500) throw new ApiError(502, 'UPSTREAM_UNAVAILABLE', 'تعذر الوصول الى الخادم، حاول مرة اخرى')
  if (own?.code) throw new ApiError(res.status, own.code, own.message || message || 'تم رفض الطلب')
  const data = (answer.data && typeof answer.data === 'object' ? answer.data : null) as Row | null
  if (answer.success === true && data) return data
  // the password step's two-step answers come with success:false and their challenge
  if (data && (data.twoFactorRequired || data.enrollmentRequired)) return data
  if (res.status === 401 || res.status === 400) throw new ApiError(401, path.endsWith('/Login') ? 'INVALID_CREDENTIALS' : 'INVALID_CODE', message || 'البيانات غير صحيحة')
  throw new ApiError(res.status >= 400 ? res.status : 401, path.endsWith('/Login') ? 'INVALID_CREDENTIALS' : 'UPSTREAM_REJECTED', message || 'اسم المستخدم او كلمة المرور غير صحيحة')
}

const direct = () => env.apiMode === 'direct'

export const authService = {
  /** The password step: the signed-in user, or the second step it still needs. */
  async login(username: string, password: string): Promise<{ user: CurrentUser; step?: undefined } | { user?: undefined; step: SignInStep }> {
    // the company this device chose on the main entry travels with the sign-in; after it the session decides
    const company = readSavedCompany()
    const headers = company ? { CompanyID: company.code } : undefined
    if (direct()) {
      const d = await compatSignIn('/dispatch/Users/V1/Login', { userName: username, password, roleID: 0 }, headers)
      if (d.twoFactorRequired && d.challenge) return { step: { kind: 'totp', challenge: String(d.challenge), expiresAt: expiry(d.expiresAt, 5 * 60_000) } }
      if (d.enrollmentRequired && d.enrollment) return { step: { kind: 'enroll', enrollment: String(d.enrollment), expiresAt: expiry(d.expiresAt, 5 * 60_000) } }
      return { user: userFromLogin(d) }
    }
    const data = (await request<LoginAnswer>('/auth/login', { method: 'POST', body: { username, password }, headers })).data
    if (data?.twoFactorRequired && data.challenge) return { step: { kind: 'totp', challenge: data.challenge, expiresAt: Number(data.expiresAt) || Date.now() + 5 * 60_000 } }
    if (data?.enrollmentRequired && data.enrollment) return { step: { kind: 'enroll', enrollment: data.enrollment, expiresAt: Number(data.expiresAt) || Date.now() + 5 * 60_000 } }
    return { user: data }
  },
  /** The code step: a 6-digit code from the app or a recovery code. */
  async loginTotp(challenge: string, code: string): Promise<SignedIn> {
    if (direct()) {
      const d = await compatSignIn('/dispatch/Users/V1/LoginTotp', { challenge, code })
      return { ...userFromLogin(d), usedRecoveryCode: d.usedRecoveryCode === true }
    }
    return (await request<SignedIn>('/auth/login/totp', { method: 'POST', body: { challenge, code } })).data
  },
  /** Enrolment during sign-in: a new secret, shown as a QR. */
  async loginEnroll(enrollment: string) {
    if (direct()) {
      const d = await compatSignIn('/dispatch/Users/V1/LoginEnroll', { enrollment })
      return { secret: String(d.secret ?? ''), otpauthUrl: String(d.otpauthUrl ?? ''), expiresAt: expiry(d.expiresAt, 5 * 60_000) }
    }
    return (await request<{ secret: string; otpauthUrl: string; expiresAt: number }>('/auth/login/enroll', { method: 'POST', body: { enrollment } })).data
  },
  /** …and its first code: signs in and returns the 10 recovery codes, once. */
  async loginEnrollConfirm(enrollment: string, code: string): Promise<SignedIn> {
    if (direct()) {
      const d = await compatSignIn('/dispatch/Users/V1/LoginEnrollConfirm', { enrollment, code })
      return { ...userFromLogin(d), recoveryCodes: arr(d.recoveryCodes) }
    }
    return (await request<SignedIn>('/auth/login/enroll/confirm', { method: 'POST', body: { enrollment, code } })).data
  },
  async logout() {
    if (direct()) {
      await request('/dispatch/Users/V1/Logout', { method: 'POST', body: {} })
      return
    }
    await request('/auth/logout', { method: 'POST', body: {} })
  },
  async me(signal?: AbortSignal) {
    if (direct()) return (await request<CurrentUser>('/backend/web/me', { signal })).data
    return (await request<CurrentUser>('/auth/me', { signal })).data
  },
  async branches() {
    if (direct()) return (await request<UserBranch[]>('/dispatch/Users/V1/GetBranches')).data ?? []
    return (await request<UserBranch[]>('/auth/branches')).data ?? []
  },
  async changeBranch(branchId: number) {
    if (direct()) {
      await request(`/dispatch/Users/V1/ChangeBranch/${branchId}`, { method: 'POST', body: {} })
      return
    }
    await request(`/auth/branch/${branchId}`, { method: 'POST', body: {} })
  },
  async changePassword(oldPassword: string, newPassword: string) {
    if (direct()) {
      await request('/dispatch/Users/V1/ChangePassword', { method: 'POST', body: { oldPassword, newPassword } })
      return
    }
    await request('/auth/change-password', { method: 'POST', body: { oldPassword, newPassword } })
  },
}
